// src/lib/metrics/index.ts
/**
 * Centralized metrics library
 * 
 * Pure functions for all financial/statistical calculations used across:
 * - TokenDashboard
 * - SectorComparisonPage
 * - SectorDashboard
 * - PortfolioPage
 * - TokenScreener
 * 
 * Usage:
 *   import { sharpeRatio, maxDrawdown, toReturns } from '@/lib/metrics';
 *   
 *   const returns = toReturns(prices);
 *   const sharpe = sharpeRatio(returns);
 */

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type {
  Series,
  ValidSeries,
  TimeSeries,
  RollingConfig,
  BenchmarkMetrics,
  RiskMetrics,
  DistributionMetrics,
  RollingMetricFn,
} from './types';

export {
  TRADING_DAYS,
  DEFAULT_RISK_FREE_RATE,
  CONFIDENCE_LEVELS,
} from './types';

// ============================================================================
// PRIMITIVES - Statistical foundations
// ============================================================================

export {
  // Utilities
  filterValid,
  clampFinite,
  // Central tendency
  mean,
  median,
  geometricMean,
  // Dispersion
  variance,
  stdDev,
  downsideDeviation,
  upsideDeviation,
  // Covariance & correlation
  covariance,
  correlation,
  correlationMatrix,
  // Aggregation
  sum,
  min,
  max,
  range,
  percentPositive,
} from './primitives';

// ============================================================================
// RETURNS - Price to returns conversion
// ============================================================================

export {
  // Conversion
  toReturns,
  toLogReturns,
  toReturnsAligned,
  // Cumulative
  toCumulative,
  pricesToCumulativePercent,
  totalReturn,
  totalReturnFromPrices,
  // Annualization
  annualizeReturn,
  annualizedReturnFromDaily,
  cagr,
  // Excess returns
  excessReturns,
  activeReturns,
  // Period aggregation
  compoundToWeekly,
  compoundToMonthly,
  // Statistics
  averageReturn,
  winRate,
  winLossStats,
} from './returns';

// ============================================================================
// RISK - VaR, ES, Drawdown
// ============================================================================

export {
  // VaR
  valueAtRisk,
  valueAtRiskLoss,
  // Expected Shortfall
  expectedShortfall,
  expectedShortfallLoss,
  // Drawdown
  drawdownSeries,
  drawdownSeriesFromReturns,
  maxDrawdown,
  maxDrawdownFromReturns,
  currentDrawdown,
  drawdownDuration,
  averageDrawdownDuration,
  medianRecoveryTime,
  // Volatility of Volatility
  volatilityOfVolatility,
  // Tail risk
  crashSensitivity,
  tailRatio,
} from './risk';

// ============================================================================
// RATIOS - Sharpe, Sortino, Calmar, Omega, etc.
// ============================================================================

export {
  // Risk-adjusted
  sharpeRatio,
  sortinoRatio,
  calmarRatio,
  omegaRatio,
  informationRatio,
  treynorRatio,
  // Capture
  upsideCapture,
  downsideCapture,
  captureSpread,
  // Hit rates
  hitRates,
} from './ratios';

// ============================================================================
// DISTRIBUTION - Skewness, Kurtosis, Quantiles
// ============================================================================

export {
  // Higher moments
  skewness,
  kurtosis,
  computeDistributionMetrics,
  // Quantiles
  quantile,
  percentileRank,
  quantiles,
  quartiles,
  iqr,
  fiveNumberSummary,
  // Z-scores
  zScore,
  zScores,
  standardize,
  // Coefficient of variation
  coefficientOfVariation,
  cvPercent,
  // Normality
  jarqueBeraStatistic,
} from './distribution';

// ============================================================================
// ROLLING - Rolling window calculations
// ============================================================================

export {
  // Generic
  rolling,
  // Volatility
  rollingVolatility,
  rollingVolatilityPercent,
  // Ratios
  rollingSharpe,
  rollingSortino,
  // Deviations
  rollingUpsideDeviation,
  rollingDownsideDeviation,
  // Drawdown
  rollingDrawdown,
  rollingMaxDrawdown,
  // Moving averages
  rollingMean,
  ema,
  wma,
  // Statistics
  rollingStdDev,
  rollingZScore,
  bollingerBands,
  // VaR & ES
  rollingVaR,
  rollingES,
  // Higher moments
  rollingSkewness,
  rollingKurtosis,
} from './rolling';

// ============================================================================
// TRENDS - MA distance, streaks, momentum, RRG
// ============================================================================

export {
  // MA Distance
  maDistance,
  maDistancePercent,
  maDistances,
  // Simple MA
  sma,
  rollingMA,
  periodsAboveMA,
  periodsAboveMAInRange,
  // ATH/ATL
  distanceFromHigh,
  daysSinceHigh,
  distanceFromLow,
  // Streaks
  calculateStreaks,
  // Momentum
  calculateMomentum,
  momentumSlope,
  momentumZScore,
  relativeStrengthRatio,
  // RRG
  calculateRRGMetrics,
  // Win rate
  calculateWinRate,
  alphaHitRate,
} from './trends';

export type {
  StreakMetrics,
  MomentumMetrics,
  WinRateMetrics,
  RRGMetrics,
} from './trends';

// ============================================================================
// CONCENTRATION - Market structure metrics
// ============================================================================

export {
  // Gini
  giniCoefficient,
  giniFromMarketCaps,
  // HHI
  hhi,
  hhiFromMarketCaps,
  effectiveNumber,
  effectiveNumberFromMarketCaps,
  // Dominance
  topKDominance,
  topKDominanceFromMarketCaps,
  // Dispersion
  crossSectionalDispersion,
  crossSectionalDispersionPercent,
  // EW vs CW
  ewCwSpread,
  ewCwSpreadPercent,
  // Participation
  participationCount,
  participationRate,
  advanceDeclineRatio,
} from './concentration';

// ============================================================================
// DERIVATIVES - Funding rates, Open Interest
// ============================================================================

export {
  // Funding rate metrics
  cumulativeFundingRate,
  fundingRateVolatility,
  fundingRatePercentPositive,
  computeFundingStats,
  // Open interest
  oiChangePct,
  // Regime
  determineRegime,
  // Extremes
  findFundingExtremes,
  // Window slicing
  sliceFundingByWindow,
} from './derivatives';

export type {
  FundingStats,
  MarketRegime,
  FundingExtreme,
  FundingExtremes,
} from './derivatives';

// ============================================================================
// REGRESSION - Alpha, Beta, Correlation
// ============================================================================

// Re-export from regression module
export {
  computeBeta,
  computeAlpha,
  computeAlphaBeta,
  computeDownsideBeta,
  computeUpsideBeta,
  computeBenchmarkMetrics,
  rollingBeta,
  rollingCorrelation,
  rollingAlpha,
  correlationStability,
} from './regression';

// Re-export correlation from primitives as pearsonCorrelation for clarity
export { correlation as pearsonCorrelation } from './primitives';

// Import for computeRollingBetaCorrelation (custom implementation needed for specific behavior)
import type { Series as _Series } from './types';

/**
 * Compute rolling beta and correlation with paired output.
 * Output length matches input length for date alignment.
 */
export function computeRollingBetaCorrelation(
  assetReturns: _Series,
  benchmarkReturns: _Series,
  window: number,
  minPoints?: number
): { rollingBeta: (number | null)[]; rollingCorrelation: (number | null)[] } {
  const n = Math.max(assetReturns.length, benchmarkReturns.length);
  const minPts = minPoints ?? Math.max(10, Math.floor(window * 0.5));
  
  const rollingBeta: (number | null)[] = [];
  const rollingCorrelation: (number | null)[] = [];
  
  for (let i = 0; i < n; i++) {
    const start = Math.max(0, i - window + 1);
    const aSlice: number[] = [];
    const bSlice: number[] = [];
    
    for (let j = start; j <= i; j++) {
      const a = assetReturns[j];
      const b = benchmarkReturns[j];
      if (
        a !== null && a !== undefined && Number.isFinite(a) &&
        b !== null && b !== undefined && Number.isFinite(b)
      ) {
        aSlice.push(a);
        bSlice.push(b);
      }
    }
    
    if (aSlice.length >= minPts) {
      const meanA = aSlice.reduce((s, v) => s + v, 0) / aSlice.length;
      const meanB = bSlice.reduce((s, v) => s + v, 0) / bSlice.length;
      
      let sumAB = 0, sumA2 = 0, sumB2 = 0;
      for (let k = 0; k < aSlice.length; k++) {
        const da = aSlice[k]! - meanA;
        const db = bSlice[k]! - meanB;
        sumAB += da * db;
        sumA2 += da * da;
        sumB2 += db * db;
      }
      
      rollingBeta.push(sumB2 > 0 ? sumAB / sumB2 : null);
      const denom = Math.sqrt(sumA2 * sumB2);
      rollingCorrelation.push(denom > 0 ? Math.max(-1, Math.min(1, sumAB / denom)) : null);
    } else {
      rollingBeta.push(null);
      rollingCorrelation.push(null);
    }
  }
  
  return { rollingBeta, rollingCorrelation };
}

// ============================================================================
// DATA UTILITIES - Slicing and alignment helpers
// ============================================================================

/**
 * Slice arrays to the last N elements (for time range selection)
 * 
 * @param dates - Array of date strings
 * @param values - Array of values aligned with dates
 * @param days - Number of days to keep from the end
 * @returns Sliced dates and values
 */
export function sliceByRange<T>(
  dates: string[],
  values: T[],
  days: number
): { dates: string[]; values: T[] } {
  const n = Math.min(days, dates.length, values.length);
  return {
    dates: dates.slice(-n),
    values: values.slice(-n),
  };
}

